import { Box, CircularProgress, Divider, Grid2 } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FaArrowRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { getCharleeAccolades } from '../api/api';
import { getUsecaseHref } from '../helper/hubItemHref';
import { HubPagination } from '../layout/HubPagination';

const USECASE_PAGE_LIMIT = 9;

function parseUsecases(accolades) {
  return accolades.map((item) => ({
    id: item.id,
    relevance: item.Relevance,
    title: item.title,
    description: item.Description,
    product: item.Product,
    area: item.Area,
  }));
}

function UsecaseCard({ usecase }) {
  const href = getUsecaseHref(usecase.relevance);

  const content = (
    <>
      <span
        className="pointer-events-none absolute inset-x-0 top-0 h-1 origin-left scale-x-0 bg-gradient-to-r from-[#22c0b1] to-[#021744] transition-transform duration-300 ease-out group-hover:scale-x-100"
        aria-hidden
      />
      <span
        className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-[#22c0b1]/0 transition-[background-color,transform] duration-500 ease-out group-hover:scale-150 group-hover:bg-[#22c0b1]/10"
        aria-hidden
      />

      <h6 className="relative line-clamp-2 text-[20px] font-medium leading-[28px] text-[#021744] transition-colors duration-300 group-hover:text-[#0099B0] sm:text-[22px] sm:leading-[30px]">
        {usecase.title}
      </h6>
      {usecase.description ? (
        <p className="relative mt-3 line-clamp-4 flex-1 text-[14px] leading-[22px] text-[#949494] transition-colors duration-300 group-hover:text-[#6b7280]">
          {usecase.description}
        </p>
      ) : null}
      {(usecase.product || usecase.area) && (
        <div className="relative mt-4 flex flex-wrap gap-2">
          {usecase.product ? (
            <span className="rounded-full bg-[#F3F4F4] px-3 py-1 text-[12px] font-medium text-[#021744] transition-colors duration-300 group-hover:bg-[#e8f7f5]">
              {usecase.product}
            </span>
          ) : null}
          {usecase.area ? (
            <span className="rounded-full bg-[#F3F4F4]/80 px-3 py-1 text-[12px] text-[#949494] transition-colors duration-300 group-hover:bg-[#F3F4F4]">
              {usecase.area}
            </span>
          ) : null}
        </div>
      )}

      <span className="relative mt-5 flex items-center gap-2 text-sm font-medium text-[#021744] opacity-0 transition-all duration-300 group-hover:opacity-100">
        View use case
        <FaArrowRight className="text-xs transition-transform duration-300 group-hover:translate-x-1" />
      </span>
    </>
  );

  const cardClassName =
    'group relative flex h-full w-full flex-col overflow-hidden rounded-2xl bg-white p-5 text-left shadow-[0_2px_16px_rgba(2,23,68,0.07)] transition-[transform,box-shadow] duration-300 ease-out hover:-translate-y-1.5 hover:shadow-[0_16px_40px_rgba(2,23,68,0.14)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#22c0b1] sm:p-6 no-underline text-inherit';

  if (!href) {
    return (
      <div className={cardClassName} aria-disabled>
        {content}
      </div>
    );
  }

  return (
    <Link to={href} className={cardClassName}>
      {content}
    </Link>
  );
}

export function UsecaseCardList() {
  const [usecasesList, setUsecasesList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const loadPage = useCallback(async (page) => {
    setLoading(true);
    try {
      const { accolades, pagination } = await getCharleeAccolades(page, USECASE_PAGE_LIMIT);
      setUsecasesList(parseUsecases(accolades));
      setTotalPages(Math.max(1, pagination.pages ?? 1));
    } catch {
      setUsecasesList([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPage(currentPage);
  }, [currentPage, loadPage]);

  const onPageChange = (_event, page) => {
    setCurrentPage(page);
  };

  return (
    <Box className="w-full text-start">
      <h4 className="my-4 text-[42px] font-medium leading-[54.6px]">Usecases</h4>

      {loading ? (
        <Box className="flex min-h-[280px] w-full items-center justify-center py-8">
          <CircularProgress />
        </Box>
      ) : usecasesList.length === 0 ? (
        <p className="py-12 text-center text-[#949494]">No use cases available.</p>
      ) : (
        <Grid2 container spacing={2} className="py-2">
          {usecasesList.map((usecase) => (
            <Grid2 key={usecase.relevance ?? usecase.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <UsecaseCard usecase={usecase} />
            </Grid2>
          ))}
        </Grid2>
      )}

      <Divider className="mt-6 border-gray-200" />
      <Box className="w-full py-6 md:py-8">
        <HubPagination
          count={totalPages}
          page={currentPage}
          onChange={onPageChange}
        />
      </Box>
    </Box>
  );
}
